import type { CoachingSession } from '@efdigital/ll-addon-data';
import type AdminSessionsService from '@efdigital/ll-admin-v2/services/admin-sessions';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { AdminActionModal } from './admin-action-modal.gts';

interface SessionCancelModalSignature {
  Args: {
    coachingSession: CoachingSession;
    onSuccess?: () => Promise<void>;
  };
  Blocks: {
    cancel: [showModal: () => void];
  };
}

export class SessionCancelModal extends Component<SessionCancelModalSignature> {
  @service declare adminSessions: AdminSessionsService;

  isReasonSet = (reason: string): boolean => reason.trim().length > 0;

  cancel = async (reason: string) => {
    await this.adminSessions.cancelSession(this.args.coachingSession.id!, {
      cancelReason: reason.trim(),
    });
    await this.args.onSuccess?.();
  };

  setReasonFromInput = (setValue: (value: string) => void, event: Event) => {
    setValue((event.target as HTMLTextAreaElement).value);
  };

  <template>
    <AdminActionModal
      @title="Cancel session"
      @submitLabel="Cancel session"
      @submittingLabel="Canceling…"
      @initialValue=""
      @isValid={{this.isReasonSet}}
      @invalidHint="Enter a cancellation reason to continue"
      @onSubmit={{this.cancel}}
    >
      <:trigger as |showModal|>
        {{yield showModal to="cancel"}}
      </:trigger>
      <:description>
        Support/ops cancellation — use this when neither the coach nor the
        coachee canceled it themselves (a coach going unexpectedly unavailable,
        a scheduling error, a dispute resolved by starting over). Quota is
        credited as an early cancellation would be, and
        <strong>both the coach and the coachee</strong>
        are notified by email.
      </:description>
      <:fields as |reason setValue isSubmitting|>
        <div class="mb-3">
          <label class="form-label required" for="cancel-reason">Cancellation
            reason</label>
          <textarea
            id="cancel-reason"
            class="form-control"
            rows="3"
            required
            placeholder="e.g. Coach became unavailable"
            disabled={{isSubmitting}}
            data-test-cancel-reason
            {{on "input" (fn this.setReasonFromInput setValue)}}
          >{{reason}}</textarea>
          <div class="form-text">Shown to both parties in their cancellation
            email.</div>
        </div>
      </:fields>
    </AdminActionModal>
  </template>
}
